import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { LEAD_SECTIONS, STATUS_TONE } from "@/lib/leadSchema";
import SocialLinks from "@/components/SocialLinks";
import {
  Search, ChevronRight, Plus, Loader2,
  CheckCircle2, HelpCircle, XCircle, RotateCcw,
} from "lucide-react";

const ALL_FIELDS = LEAD_SECTIONS.flatMap((s) => s.fields).map((f) => ({ name: f.name, label: f.label }));
const ASSIGN_COLUMNS = [
  { name: "assigned_auditor", label: "Assigned Auditor" },
  { name: "assigned_email_marketing", label: "Assigned EM" },
  { name: "assigned_team_lead", label: "Assigned TL" },
  { name: "assigned_agent", label: "Assigned Agent" },
];
const MONO_FIELDS = new Set([
  "work_email", "alternate_email", "domain", "website", "lead_id",
  "linkedin_company_link", "linkedin_prospect_link", "revenue_link",
  "work_phone", "direct_number", "ext", "zip_code",
]);

const QA_STATUSES = ["", "Approved", "Pending", "Rejected", "Re-audit", "Invalid-data"];

// Full call-disposition set (matches backend `CALL_DISPOSITIONS`).
// Grouped for the dropdown so the agent's eye lands on the productive
// outcomes first without hiding the non-productive ones.
const DISPOSITION_GROUPS = [
  { label: "Productive", options: ["Lead", "RPC Verified", "VMRPC", "Operator Verified", "RPC Voice Mail Without Name", "Operator Voicemail"] },
  { label: "Attempts",   options: ["Voicemail", "Hung Up", "Call Failed", "Call Back", "Company IVR"] },
  { label: "Rejections", options: ["Invalid contact", "Language Barrier", "Not Interested", "No Longer Working With The Company", "Invalid Job Title", "Dead Contact", "Duplicate"] },
];
const _VERIFICATION_PRODUCTIVE = new Set(["RPC Verified", "VMRPC", "Operator Verified", "RPC Voice Mail Without Name"]);
const isProductive = (campaignType, value) => {
  if (!value) return false;
  if ((campaignType || "").toLowerCase() === "verification") return _VERIFICATION_PRODUCTIVE.has(value);
  return value === "Lead";
};

export default function MyLeads() {
  const { user, options } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [campaign, setCampaign] = useState("");
  const [qaStatus, setQaStatus] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [canCreate, setCanCreate] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/permissions/effective");
        const pp = data.page_permissions || {};
        // Show "Add Lead" only when the user's role has explicit new_lead access.
        if (!cancelled) setCanCreate(pp.new_lead === true);
      } catch {
        if (!cancelled) setCanCreate(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.email]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      // Fetch up to 5000 assigned leads. TLs regularly bulk-assign 500+
      // to a single agent during a campaign push; the older 200-row
      // ceiling caused a "partial assignment" bug where the assignment
      // succeeded on the backend but the agent's UI only showed the
      // newest 200 leads — the older ones looked unassigned.
      const { data } = await api.get("/leads/mine", {
        params: { q: q || undefined, campaign: campaign || undefined, limit: 5000 },
      });
      let rows = data.items || [];
      // If the DB actually has MORE assigned leads than we could fetch
      // in one page, surface it clearly so admins/TLs can spot the
      // truncation instead of assuming the assign call failed.
      if ((data.total || 0) > rows.length) {
        toast.warning(
          `Showing ${rows.length} of ${data.total} assigned leads — pagination needed for the rest.`,
          { duration: 6000 },
        );
      }
      if (qaStatus) rows = rows.filter((l) => (l.qa_audit_status || "") === qaStatus);
      setItems(rows);
    } catch (e) {
      toast.error("Failed to load your leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLeads(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { openCount, doneCount, visible } = useMemo(() => {
    const open = items.filter((l) => !l.agent_disposition);
    const done = items.length - open.length;
    return { openCount: open.length, doneCount: done, visible: showAll ? items : open };
  }, [items, showAll]);

  const setDisposition = async (lead, nextValue) => {
    const value = lead.agent_disposition === nextValue ? "" : nextValue;
    setBusyId(lead.id);
    setItems((prev) => prev.map((l) => l.id === lead.id ? { ...l, agent_disposition: value } : l));
    try {
      await api.post(`/leads/${lead.id}/disposition`, { value });
      if (value) toast.success(`Marked as ${value}`);
      else toast.success("Disposition cleared");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
      setItems((prev) => prev.map((l) => l.id === lead.id ? { ...l, agent_disposition: lead.agent_disposition || "" } : l));
    } finally {
      setBusyId(null);
    }
  };

  const myEmail = (user?.email || "").toLowerCase();
  const ASSIGN_KEYS_LABEL = {
    assigned_team_lead: "Team Lead",
    assigned_agent: "Agent",
    assigned_auditor: "Auditor",
    assigned_email_marketing: "EM",
  };

  return (
    <div data-testid="my-leads-page" className="flex flex-col h-full">
      <PageHeader
        eyebrow={`Signed in as ${user?.email || ""}`}
        title="My Assigned Leads"
        description={`${openCount} open · ${doneCount} dispositioned. Same column set as the Admin Leads table — click "Open" to edit any record with full-field access.`}
        testid="my-leads-header"
        action={
          canCreate ? (
            <Link to="/leads/new">
              <Button data-testid="my-leads-add-lead" className="rounded-sm bg-primary hover:bg-[#00227A] h-10">
                <Plus size={16} className="mr-1" /> Add Lead
              </Button>
            </Link>
          ) : null
        }
      />
      <div className="p-8 space-y-4 flex-1 min-h-0 flex flex-col">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[280px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
            <Input
              data-testid="my-leads-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchLeads()}
              placeholder="Search by email, domain, name, company, lead ID…"
              className="rounded-sm h-10 pl-9 font-mono"
            />
          </div>
          <select
            data-testid="my-leads-campaign-filter"
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            className="rounded-sm h-10 px-3 border border-input bg-background text-sm"
          >
            <option value="">All Campaigns</option>
            {(options?.campaigns ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            data-testid="my-leads-qa-filter"
            value={qaStatus}
            onChange={(e) => setQaStatus(e.target.value)}
            className="rounded-sm h-10 px-3 border border-input bg-background text-sm"
          >
            {QA_STATUSES.map((s) => (
              <option key={s} value={s}>{s ? `QA: ${s}` : "All QA statuses"}</option>
            ))}
          </select>
          <Button
            data-testid="my-leads-apply"
            onClick={fetchLeads}
            className="rounded-sm h-10"
            variant="outline"
          >
            Apply
          </Button>
          <label className="inline-flex items-center gap-2 ml-1 px-3 border border-border rounded-sm h-10 text-xs cursor-pointer hover:bg-secondary/40" data-testid="my-leads-show-all-toggle">
            <input
              type="checkbox"
              checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)}
              className="accent-primary"
            />
            Show dispositioned ({doneCount})
          </label>
        </div>

        <div
          className="border border-border overflow-x-auto overscroll-contain flex-1 min-h-[400px]"
          data-testid="my-leads-table-scroll"
          style={{ touchAction: "pan-x pan-y" }}
        >
          <table className="w-full text-sm">
            <thead className="bg-secondary/40">
              <tr className="text-left border-b border-border">
                <th className="px-3 py-2 meta-label sticky left-0 bg-secondary/80 z-10 whitespace-nowrap">Assigned as</th>
                <th className="px-3 py-2 meta-label whitespace-nowrap">Quick Action</th>
                {ALL_FIELDS.map((f) => (
                  <th key={f.name} className="px-3 py-2 meta-label whitespace-nowrap">{f.label}</th>
                ))}
                {ASSIGN_COLUMNS.map((f) => (
                  <th key={f.name} className="px-3 py-2 meta-label whitespace-nowrap">{f.label}</th>
                ))}
                <th className="px-3 py-2 meta-label whitespace-nowrap">Created</th>
                <th className="px-3 py-2 meta-label whitespace-nowrap">Social Links</th>
                <th className="px-3 py-2 meta-label sticky right-0 bg-secondary/80 z-10"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={ALL_FIELDS.length + ASSIGN_COLUMNS.length + 5} className="px-3 py-10 text-center text-muted-foreground">
                    <Loader2 className="inline-block animate-spin mr-2" size={14} />
                    Loading your assigned leads…
                  </td>
                </tr>
              )}
              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={ALL_FIELDS.length + ASSIGN_COLUMNS.length + 5} className="px-3 py-12 text-center text-muted-foreground" data-testid="my-leads-empty">
                    {items.length === 0
                      ? "Nothing assigned to you yet. Once an admin assigns a lead to your account, it will appear here."
                      : `All ${items.length} of your assigned leads are dispositioned. Toggle "Show dispositioned" to view them.`}
                  </td>
                </tr>
              )}
              {!loading && visible.map((l) => {
                const current = l.agent_disposition || "";
                const campType = l.campaign_type || "";
                const productive = isProductive(campType, current);
                const assignedAs = ASSIGN_COLUMNS
                  .filter((c) => (l[c.name] || "").toLowerCase() === myEmail)
                  .map((c) => ASSIGN_KEYS_LABEL[c.name]);
                return (
                  <tr
                    key={l.id}
                    className={`border-b border-border last:border-0 hover:bg-secondary/40`}
                    data-testid={`my-lead-row-${l.id}`}
                  >
                    <td className="px-3 py-2 sticky left-0 bg-background z-0 whitespace-nowrap">
                      {assignedAs.length === 0 ? (
                        <span className="text-muted-foreground text-xs">—</span>
                      ) : (
                        <div className="inline-flex gap-1">
                          {assignedAs.map((r) => (
                            <span key={r} className="text-[10px] px-1.5 py-0.5 rounded-sm badge-info" data-testid={`assigned-as-${r.replace(/\s+/g, "-").toLowerCase()}-${l.id}`}>{r}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap" data-testid={`disposition-cell-${l.id}`}>
                      <div className="inline-flex items-center gap-2">
                        <select
                          value={current}
                          onChange={(e) => setDisposition(l, e.target.value === current ? current : e.target.value)}
                          disabled={busyId === l.id}
                          className={`h-6 text-[11px] rounded-sm border px-1.5 min-w-[170px] bg-background ${
                            productive ? "border-emerald-400 text-emerald-800 bg-emerald-50" :
                            current    ? "border-slate-300 text-slate-700 bg-slate-50" :
                                         "border-slate-300 text-slate-500"}`}
                          data-testid={`disposition-select-${l.id}`}
                        >
                          <option value="">— Set disposition —</option>
                          {DISPOSITION_GROUPS.map((g) => (
                            <optgroup key={g.label} label={g.label}>
                              {g.options.map((v) => (
                                <option key={v} value={v}>{v}</option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                        {productive && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm badge-success" title="Counts toward productivity">
                            +1
                          </span>
                        )}
                        {busyId === l.id && <Loader2 size={10} className="animate-spin text-muted-foreground" />}
                      </div>
                    </td>
                    {ALL_FIELDS.map((f) => {
                      const v = l[f.name];
                      if (f.name === "qa_audit_status") {
                        return (
                          <td key={f.name} className="px-3 py-2 whitespace-nowrap">
                            {v ? (
                              <span className={`text-[10px] px-2 py-0.5 rounded-sm ${STATUS_TONE[v] || "badge-neutral"}`}>{v}</span>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </td>
                        );
                      }
                      const cls = MONO_FIELDS.has(f.name) ? "font-mono text-xs" : "";
                      return (
                        <td key={f.name} className={`px-3 py-2 whitespace-nowrap ${cls}`}>{v || ""}</td>
                      );
                    })}
                    {ASSIGN_COLUMNS.map((f) => (
                      <td key={f.name} className="px-3 py-2 font-mono text-xs whitespace-nowrap">{l[f.name] || ""}</td>
                    ))}
                    <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                      {(l.created_at || "").slice(0, 10)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <SocialLinks lead={l} />
                    </td>
                    <td className="px-3 py-2 sticky right-0 bg-background">
                      <Link
                        to={`/leads/${l.id}`}
                        data-testid={`open-my-lead-${l.id}`}
                        className="text-xs font-mono text-primary hover:underline inline-flex items-center gap-1"
                      >
                        Open <ChevronRight size={12} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
